import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Box, Button, Flex, Text, useColorMode } from 'theme-ui';
import DeliveriesTable from './DeliveriesTable';
import theme from '../../../theme';
import {
  DeliveryRecord,
  IActionHighlightState,
  IDropdownOption,
  IMasterDataState,
  IProjectManagerItem,
  IRawDeliveryItem,
  IStageMasterItem
} from '../../../../@types';

import { getAllDeliveries, UpdateDeliveryRow } from '../../../store/actions/action.Highlighters';
import { useQuery } from '@tanstack/react-query';
import { baseUrl } from '../../../utils/global';
import { priorityOptions } from '../../../utils/constent';
import { ActionDeleteById, ActionUpdateById } from '../../../store/actions/CommonAction';
import { getApiEndPoint, removeDuplicateQueryParams } from '../../../utils/GenerateQueryString';
import toast from 'react-hot-toast';
import EditDeliveryDetailsModal from './EditDeliveryDetailsModal';
import AddManualDeliveryModal from './AddManualDeliveryModal';
import { DeletePopModule } from '../../DeletePopModule/DeletePopModule';
import ClearTableFilter from '../../Button/ClearTableFilter';
import { DeliveriesPageProps } from './types';
import useDeliveryColumns from './useDeliveryColumns';
import { formatDeliveriesData } from './deliveryModalUtils';

const PUBLISHING_READY_OPTIONS: IDropdownOption[] = [
  { label: 'Ready', value: 'true' },
  { label: 'Not Ready', value: 'false' },
];

const DELIVERY_TABS: Array<{ id: 'ready' | 'completed' | 'manual'; label: string }> = [
  { id: 'ready', label: 'Ready to Deliver' },
  { id: 'completed', label: 'Completed Deliveries' },
  { id: 'manual', label: 'Manual Deliveries' },
];

const TARGET_STATUSES = [
  'Ready To Deliver',
  'IPR Ready To Deliver',
  'Delivered',
  'IPR Delivered',
];

const IGNORED_FILTER_KEYS = new Set(['currentPage', 'limit', 'page', 'openStageId', 'istrue']);
const DEFAULT_FILTER_KEYS = new Set(['_id', 'manualDeliver']);

const getDeliveryQueryPath = (id: string, isManual: boolean) =>
  isManual ? `/deliveries/manual-delivery-items/${id}` : `/deliveries/edit-delivery/${id}`;

const getPersonName = (p?: { FirstName?: string; LastName?: string; name?: string } | null) =>
  p ? `${p.FirstName || ''} ${p.LastName || ''}`.trim() || p.name || '' : '';

const getColumnConfigs = (stageAccessorKey: string) => [
  { accessorKey: 'no', filterType: 'textbox', header: 'No.' },
  { accessorKey: stageAccessorKey, filterType: 'dropdown', header: 'Stage Name' },
  { accessorKey: 'projectId', filterType: 'dropdown', header: 'Project Name' },
  { accessorKey: 'folderNumber', filterType: 'dropdown', header: 'Folder Number' },
  { accessorKey: 'manager', filterType: 'dropdown', header: 'PM Manager' },
  { accessorKey: 'cadOperator', filterType: 'dropdown', header: 'CAD Operator' },
  { accessorKey: 'deliveryDate', filterType: 'date', header: 'Delivery Date' },
  { accessorKey: 'status', filterType: 'dropdown', header: 'Status' },
  { accessorKey: 'priority', filterType: 'dropdown', header: 'Priority' },
  { accessorKey: 'publishingReady', filterType: 'dropdown', header: 'Publishing Ready' },
];

const DeliveriesPage: React.FC<DeliveriesPageProps> = ({ defaultTab }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [colorMode] = useColorMode();
  const isDark = colorMode === 'dark';
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: 'ready' | 'completed' | 'manual' =
    defaultTab ||
    (location.pathname.includes('manual-deliveries')
      ? 'manual'
      : location.pathname.includes('completed-deliveries')
        ? 'completed'
        : 'ready');

  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [selectedEditRecord, setSelectedEditRecord] = useState<DeliveryRecord | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isAddManualModalOpen, setIsAddManualModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [deleteItemId, setDeleteItemId] = useState<string | null>(null);
  const [hasSavedChanges, setHasSavedChanges] = useState<boolean>(false);
  const prevSearchRef = useRef<string | null>(null);

  const limit = parseInt(searchParams.get('limit') || '15', 10);
  const currentPage = parseInt(searchParams.get('currentPage') || '1', 10);

  const { allDeliveries, loader } = useSelector(
    (state: { actionHighlight?: IActionHighlightState }) => state.actionHighlight || {}
  );
  const { dataForTable, masterdataByKeys } = useSelector(
    (state: { masterData?: IMasterDataState & { masterdataByKeys?: { data?: { allProjects?: Array<{ _id: string; name: string }> } } } }) => state.masterData || {}
  );
  const { auther } = useSelector(
    (state: { auth?: { auther?: { Role?: Array<{ RoleName?: string } | string> } } }) => state?.auth || {}
  );
  const allTasks = useSelector((state: {
    project?: { createprojectMasterData?: { allTask?: { data?: Array<Record<string, unknown>> } } };
    masterData?: { dataForTable?: { data?: { allTask?: Array<Record<string, unknown>> }; allTask?: Array<Record<string, unknown>> } };
  }) => {
    const pTasks = state.project?.createprojectMasterData?.allTask?.data;
    if (Array.isArray(pTasks) && pTasks.length > 0) return pTasks;
    const mTasks = state.masterData?.dataForTable?.data?.allTask || state.masterData?.dataForTable?.allTask;
    return Array.isArray(mTasks) && mTasks.length > 0 ? mTasks : [];
  });

  const isEditAllowed = useMemo(() => {
    if (!Array.isArray(auther?.Role)) return false;
    return (auther?.Role || []).some((r) => {
      const roleName = String((typeof r === 'object' && r ? (r as { RoleName?: string }).RoleName : r) || '').trim().toLowerCase();
      return roleName === 'super admin' || roleName === 'project manager';
    });
  }, [auther]);

  useEffect(() => {
    if (prevSearchRef.current !== null) {
      const prevParams = new URLSearchParams(prevSearchRef.current);
      const currentParams = new URLSearchParams(location.search);

      const onlyPaginationChanged =
        prevParams.get('limit') !== currentParams.get('limit') ||
        prevParams.get('currentPage') !== currentParams.get('currentPage');

      ['limit', 'currentPage', 'openStageId'].forEach((k) => {
        prevParams.delete(k);
        currentParams.delete(k);
      });

      if (prevParams.toString() !== currentParams.toString() && !onlyPaginationChanged) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('currentPage', '1');
          return next;
        }, { replace: true });
      }
    }
    prevSearchRef.current = location.search;
  }, [location.search, setSearchParams]);

  const userOptions = useMemo<IDropdownOption[]>(() => {
    const dataObj = ((dataForTable?.data || dataForTable) as Record<string, unknown> | undefined) || {};
    const pms = (Array.isArray(dataObj.allProjectManagers) ? dataObj.allProjectManagers : []) as IProjectManagerItem[];
    const map = new Map<string, string>();

    pms.forEach((pm) => {
      const name = getPersonName(pm);
      if (pm._id && name) map.set(pm._id, name);
    });

    deliveries.forEach((d) => {
      const projPm = typeof d.projectId === 'object' && d.projectId ? (d.projectId as { projectManager?: IProjectManagerItem }).projectManager : undefined;
      const pmId = projPm?._id;
      const pmName = getPersonName(projPm) || d.pmManager || '';
      if (pmId && pmName) map.set(pmId, pmName);

      const opObj = typeof d.cadOperator === 'object' && d.cadOperator ? (d.cadOperator as IProjectManagerItem) : null;
      const opId = opObj?._id || String(d.cadOperator || '');
      const rawName = typeof d.cadOperator === 'string' && !/^[0-9a-fA-F]{24}$/.test(d.cadOperator)
        ? d.cadOperator
        : '';
      const opName = getPersonName(opObj) || rawName;
      if (opId && opName) map.set(opId, opName);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ label: name, value: id }));
  }, [dataForTable, deliveries]);

  const stageOptions = useMemo<IDropdownOption[]>(() => {
    if (dataForTable?.data?.allStages && Array.isArray(dataForTable.data.allStages)) {
      return dataForTable.data.allStages.map((s: IStageMasterItem) => ({
        label: s.name,
        value: activeTab === 'manual' ? (s._id || s.name) : s.name,
      }));
    }
    const unique = Array.from(new Set(deliveries.map((d) => d.temporaryName || d.stageName).filter(Boolean)));
    return unique.map((name) => ({ label: name, value: name }));
  }, [dataForTable, deliveries, activeTab]);

  const masterProjectsList = useMemo(() => {
    const dataObj = masterdataByKeys?.data || dataForTable?.data || {};
    const allDelivered = ((dataObj as Record<string, unknown>)?.allDeliveredProjects || (dataForTable?.data as Record<string, unknown> | undefined)?.allDeliveredProjects || []) as Array<{ _id?: string; name?: string; folderNumber?: string; projectManager?: { FirstName?: string; LastName?: string; name?: string; _id?: string } }>;
    const allRTD = ((dataObj as Record<string, unknown>)?.allRTDProjects || (dataForTable?.data as Record<string, unknown> | undefined)?.allRTDProjects || []) as Array<{ _id?: string; name?: string; folderNumber?: string; projectManager?: { FirstName?: string; LastName?: string; name?: string; _id?: string } }>;
    const allDraft = ((dataObj as Record<string, unknown>)?.allDraftProjects || (dataForTable?.data as Record<string, unknown> | undefined)?.allDraftProjects || []) as Array<{ _id?: string; name?: string; folderNumber?: string; projectManager?: { FirstName?: string; LastName?: string; name?: string; _id?: string } }>;
    const allProjects = ((dataObj as Record<string, unknown>)?.allProjects || dataForTable?.data?.allProjects || []) as Array<{ _id?: string; name?: string; folderNumber?: string; projectManager?: { FirstName?: string; LastName?: string; name?: string; _id?: string } }>;
    return [...allDelivered, ...allRTD, ...allDraft, ...allProjects];
  }, [dataForTable, masterdataByKeys]);

  const projectOptions = useMemo<IDropdownOption[]>(() => {
    const map = new Map<string, string>();

    masterProjectsList.forEach((p) => {
      if (p && p._id && p.name && !map.has(p._id)) {
        map.set(p._id, p.name);
      }
    });

    deliveries.forEach((d) => {
      const id = typeof d.projectId === 'object' && d.projectId ? d.projectId._id : String(d.projectId || '');
      const name = typeof d.projectId === 'object' && d.projectId ? d.projectId.name : (d.projectName || '');
      if (id && name && !map.has(id)) {
        map.set(id, name);
      }
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ label: name, value: id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [masterProjectsList, deliveries]);

  const deliveryTypeOptions = useMemo<IDropdownOption[]>(() => {
    const unique = new Set<string>();
    deliveries.forEach((d) => {
      if (d.deliveryOptions && Array.isArray(d.deliveryOptions)) {
        d.deliveryOptions.forEach((opt) => {
          if (opt.value) unique.add(opt.value);
        });
      } else if (d.delivery) {
        unique.add(d.delivery);
      }
    });
    return Array.from(unique).map((val) => ({ label: val, value: val }));
  }, [deliveries]);

  const statusOptions = useMemo<IDropdownOption[]>(() => {
    const rawStatuses = dataForTable?.data?.status;
    const apiStatuses = Array.isArray(rawStatuses) ? rawStatuses : [];
    const statusMap = new Map(apiStatuses.map((val) => {
      const s = String(val.value || val.name || val.label || '');
      return [s.toLowerCase(), s];
    }));
    return TARGET_STATUSES.map((target) => {
      const matched = statusMap.get(target.toLowerCase());
      return { label: matched || target, value: matched || target };
    });
  }, [dataForTable]);

  const deliveriesRef = useRef(deliveries);
  useEffect(() => {
    deliveriesRef.current = deliveries;
  }, [deliveries]);

  const cleanSearchString = useMemo(() => {
    const p = new URLSearchParams(location.search);
    p.delete('openStageId');
    return p.toString();
  }, [location.search]);

  const fetchDeliveriesData = useCallback(() => {
    const stageAccessorKey = activeTab === 'manual' ? 'stageId' : 'name';
    const apiEndPoint = getApiEndPoint(
      { limit, currentPage },
      location.search,
      getColumnConfigs(stageAccessorKey),
      location.pathname
    );

    let queryParams = removeDuplicateQueryParams(apiEndPoint + location.search.replace('?', '&'));
    const urlParams = new URLSearchParams(queryParams);
    urlParams.delete('openStageId');

    let dynamicFilterArr: Record<string, unknown>[] = [];
    if (urlParams.has('dynamicFilter')) {
      try {
        dynamicFilterArr = JSON.parse(urlParams.get('dynamicFilter') || '[]');
      } catch (e) {
        dynamicFilterArr = [];
      }
    }

    if (activeTab === 'manual' && dynamicFilterArr.length > 0) {
      dynamicFilterArr = dynamicFilterArr.map((item) => {
        if (item.key === 'name' || item.key === 'stageId') {
          const valStr = String(item.value || '');
          let stageIdVal = valStr;
          const match = deliveriesRef.current.find((d) => {
            const sObj = typeof d.stageId === 'object' && d.stageId ? d.stageId : null;
            const sId = sObj?._id || String(d.stageId || '');
            return (d.temporaryName || d.stageName) === valStr || d.stageName === valStr || sId === valStr;
          });
          if (match) {
            const sObj = typeof match.stageId === 'object' && match.stageId ? match.stageId : null;
            const foundId = sObj?._id || (typeof match.stageId === 'string' ? match.stageId : '');
            if (foundId) {
              stageIdVal = foundId;
            }
          }
          return {
            ...item,
            key: 'stageId',
            value: stageIdVal,
          };
        }
        return item;
      });
    }

    urlParams.set('tabType', activeTab);
    if (dynamicFilterArr.length > 0) {
      urlParams.set('dynamicFilter', JSON.stringify(dynamicFilterArr));
    }
    queryParams = `?${urlParams.toString()}`;

    dispatch(getAllDeliveries(queryParams));
  }, [dispatch, currentPage, limit, cleanSearchString, location.pathname, activeTab]);

  useEffect(() => {
    fetchDeliveriesData();
  }, [fetchDeliveriesData]);

  const activeTabRef = useRef(activeTab);
  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  const { data: eventSource } = useQuery({
    queryKey: ['deliveriesSSE'],
    queryFn: () => {
      const eventSource = new EventSource(`${baseUrl}sse/deliveries`);

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const currentTab = activeTabRef.current;

          const isItemCompleted =
            data?.category === 'completedDeliveries' ||
            (Boolean(data?.data?.publishingReady) && Boolean(data?.data?.projectManagerDeliver));

          const shouldRemoveFromCurrentTab =
            data?.type === 'Delete' ||
            (currentTab === 'ready' && isItemCompleted) ||
            (currentTab === 'completed' && data?.category === 'readyToDeliver');

          if (shouldRemoveFromCurrentTab) {
            dispatch(UpdateDeliveryRow({ ...data, type: 'Delete' }));
          } else if (data?.type === 'Insert' || (currentTab === 'completed' && isItemCompleted)) {
            dispatch(UpdateDeliveryRow({ ...data, type: 'Insert' }));
          } else {
            dispatch(UpdateDeliveryRow(data));
          }
        } catch (error) {
          console.error('Error parsing SSE data:', error);
        }
      };

      eventSource.onerror = (error) => {
        console.error('SSE Error:', error);
        // eventSource.close();
      };
      return eventSource;
    },
    refetchInterval: 60000,
  });

  useEffect(() => {
    return () => {
      eventSource?.close();
    };
  }, [eventSource]);

  useEffect(() => {
    let rawData: IRawDeliveryItem[] = [];
    let isResponseReceived = false;

    if (Array.isArray(allDeliveries)) {
      rawData = allDeliveries;
      isResponseReceived = true;
    } else if (allDeliveries && typeof allDeliveries === 'object' && 'data' in allDeliveries) {
      rawData = Array.isArray(allDeliveries.data) ? allDeliveries.data : [];
      isResponseReceived = true;
    }

    if (isResponseReceived) {
      setDeliveries(formatDeliveriesData(rawData, currentPage, limit, dataForTable?.data?.allStages, masterProjectsList));
    }
  }, [allDeliveries, currentPage, limit, dataForTable?.data?.allStages, masterProjectsList]);

  useEffect(() => {
    setDeliveries([]);
  }, [location.pathname]);

  const openStageId = searchParams.get('openStageId');

  useEffect(() => {
    if (openStageId && deliveries.length > 0) {
      const match = deliveries.find(
        (d) => String(d.id) === String(openStageId) || String((d as unknown as { _id?: string })._id) === String(openStageId)
      );
      if (match && (!selectedEditRecord || selectedEditRecord.id !== match.id)) {
        setSelectedEditRecord(match);
        setIsEditModalOpen(true);
      }
    }
  }, [openStageId, deliveries, selectedEditRecord]);

  const handleOpenEditModal = useCallback((record: DeliveryRecord) => {
    setSelectedEditRecord(record);
    if (activeTab === 'manual') {
      setIsAddManualModalOpen(true);
    } else {
      setIsEditModalOpen(true);
      setHasSavedChanges(false);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (next.get('openStageId') !== record.id) {
            next.set('openStageId', record.id);
          }
          return next;
        },
        { replace: true }
      );
    }
  }, [activeTab, setSearchParams]);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedEditRecord(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.has('openStageId')) {
          next.delete('openStageId');
        }
        return next;
      },
      { replace: true }
    );

    if (hasSavedChanges) {
      fetchDeliveriesData();
      setHasSavedChanges(false);
    }
  }, [hasSavedChanges, fetchDeliveriesData, setSearchParams]);

  const updateDeliveryField = useCallback(
    (
      id: string,
      fieldUpdates: Partial<DeliveryRecord>,
      apiPayload: Record<string, unknown>,
      successMsg: string,
      onSuccess?: () => void
    ) => {
      const currentItem = deliveries.find((item) => item.id === id);
      setDeliveries((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...fieldUpdates } : item))
      );

      const isManual = activeTab === 'manual' || Boolean(currentItem?.manualDeliver);
      const queryPath = getDeliveryQueryPath(id, isManual);

      dispatch(
        ActionUpdateById(
          {
            Query: queryPath,
            obj: apiPayload,
            customMessage: successMsg,
          },
          (res: { status?: boolean }) => {
            if (res?.status !== false && onSuccess) {
              onSuccess();
            }
          }
        )
      );
    },
    [deliveries, dispatch, activeTab]
  );

  const togglePublishingReady = useCallback(
    (id: string) => {
      const item = deliveries.find((d) => d.id === id);
      const newValue = !item?.publishingReady;
      updateDeliveryField(
        id,
        { publishingReady: newValue },
        { publishingReady: newValue },
        'Publishing Ready updated successfully'
      );
    },
    [deliveries, updateDeliveryField]
  );

  const togglePmDelivered = useCallback(
    (id: string) => {
      const item = deliveries.find((d) => d.id === id);
      const newValue = !item?.pmDelivered;
      updateDeliveryField(
        id,
        { pmDelivered: newValue },
        { projectManagerDeliver: newValue },
        'PM Delivered updated successfully'
      );
    },
    [deliveries, updateDeliveryField]
  );

  const handleCadOperatorChange = useCallback(
    (id: string, value: string, option?: IDropdownOption | null) => {
      const operatorObj = option || userOptions.find((op) => op.value === value || op.label === value);
      const cadOperatorId = operatorObj?.value || value;
      if (cadOperatorId) {
        updateDeliveryField(
          id,
          { cadOperator: operatorObj ? { _id: cadOperatorId, FirstName: operatorObj.label } : value },
          { cadOperator: cadOperatorId },
          'CAD Operator updated successfully'
        );
      }
    },
    [userOptions, updateDeliveryField]
  );

  const handlePriorityChange = useCallback(
    (id: string, value: string) => {
      updateDeliveryField(
        id,
        { priority: value },
        { priority: value },
        'Priority updated successfully'
      );
    },
    [updateDeliveryField]
  );

  const handleDeliveryDateChange = useCallback(
    (id: string, newDate: string) => {
      updateDeliveryField(
        id,
        { deliveryDate: newDate },
        { deliveryDate: newDate },
        'Delivery Date updated successfully'
      );
    },
    [updateDeliveryField]
  );

  const handleDeleteManualItem = useCallback((id: string) => {
    setDeleteItemId(id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!deleteItemId) return;
    dispatch(
      ActionDeleteById(
        { Query: `/deliveries/manual-delivery-items/${deleteItemId}` },
        (res: { status?: boolean }) => {
          if (res?.status !== false) {
            dispatch(UpdateDeliveryRow({ id: deleteItemId, type: 'Delete' }));
          }
        }
      )
    );
  }, [deleteItemId, dispatch]);

  const totalCount = useMemo(() => {
    if (loader) return 0;
    return typeof allDeliveries === 'object' && !Array.isArray(allDeliveries) && typeof allDeliveries?.count === 'number'
      ? allDeliveries.count
      : deliveries.length;
  }, [loader, allDeliveries, deliveries.length]);

  const hasActiveFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const keys = Array.from(params.keys()).filter((key) => !IGNORED_FILTER_KEYS.has(key));
    if (keys.length === 0) return false;

    if (keys.length === 1 && keys[0] === 'dynamicFilter') {
      try {
        const arr = JSON.parse(params.get('dynamicFilter') || '[]');
        return Array.isArray(arr) && arr.some((f: { key?: string }) => f?.key && !DEFAULT_FILTER_KEYS.has(f.key));
      } catch (e) {
        return false;
      }
    }
    return true;
  }, [location.search]);

  const columns = useDeliveryColumns({
    activeTab,
    currentPage,
    limit,
    isDark,
    isEditAllowed,
    userOptions,
    stageOptions,
    projectOptions,
    deliveryTypeOptions,
    statusOptions,
    priorityOptions,
    publishingReadyOptions: PUBLISHING_READY_OPTIONS,
    allTasks: allTasks as Array<Record<string, unknown>>,
    onOpenEditModal: handleOpenEditModal,
    onCadOperatorChange: handleCadOperatorChange,
    onDeliveryDateChange: handleDeliveryDateChange,
    onPriorityChange: handlePriorityChange,
    onTogglePublishingReady: togglePublishingReady,
    onTogglePmDelivered: togglePmDelivered,
    onDeleteManualItem: handleDeleteManualItem,
  });

  const handleTabChange = (tab: 'ready' | 'completed' | 'manual') => {
    if (tab === activeTab) {
      return;
    }

    const targetPath =
      tab === 'manual'
        ? '/deliveries/manual-deliveries'
        : tab === 'completed'
          ? '/deliveries/completed-deliveries'
          : '/deliveries/ready-to-deliver';

    navigate(targetPath);
  };

  return (
    <div className="main-content deliveries-page-container">
      <Flex
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px 0 24px',
        }}
      >
        <Flex sx={{ alignItems: 'center', gap: '16px' }}>
          <Text
            variant="Poppins24Medium"
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              color: isDark ? '#ffffff' : '#0A0A0A',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            Deliveries
            <Badge
              as="span"
              className="badge"
              variant="DropDownbedge"
              sx={{
                position: 'static',
                marginTop: 0,
                color: isDark ? '#dadada' : '#0a0a0a',
                fontSize: '14px',
                fontWeight: 600,
                padding: '2px 10px',
                borderRadius: '20px',
                border: `1px solid ${isDark ? '#374358' : '#F7EFE5'}`,
                backgroundColor: isDark ? '#282e42' : 'rgba(0, 0, 0, 0.06)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {totalCount}
            </Badge>
          </Text>

          {hasActiveFilter && <ClearTableFilter url={location.pathname} ml="10px" />}
        </Flex>

        {activeTab === 'manual' && isEditAllowed && (
          <Button
            variant="AddManualButton"
            onClick={() => {
              setSelectedEditRecord(null);
              setIsAddManualModalOpen(true);
            }}
          >
            + Add Manual Item
          </Button>
        )}
      </Flex>

      <Flex
        sx={{
          borderBottom: `1px solid ${isDark ? '#374358' : '#E5E7EB'}`,
          paddingLeft: '24px',
          gap: '32px',
          marginTop: '16px',
          marginBottom: '20px',
        }}
      >
        {DELIVERY_TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <Box
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              sx={{
                paddingBottom: '12px',
                cursor: 'pointer',
                borderBottom: isActive ? `2px solid ${(theme?.colors?.primary as string) || '#7743DB'}` : 'none',
              }}
            >
              <Text
                sx={{
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? ((theme?.colors?.primary as string) || '#7743DB') : isDark ? '#A0AEC0' : '#6B7280',
                  fontSize: '15px',
                }}
              >
                {tab.label}
              </Text>
            </Box>
          );
        })}
      </Flex>

      <DeliveriesTable
        loader={Boolean(loader)}
        columns={columns}
        data={deliveries}
        totalCount={totalCount}
        perPageCount={limit}
        currentPage={currentPage}
        enableColumnFilters={true}
        onPerPageChange={(itemsPerPage) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('limit', itemsPerPage.toString());
            next.set('currentPage', '1');
            return next;
          });
        }}
        onPageChange={(value: string | number) => {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('currentPage', value.toString());
            next.set('limit', limit.toString());
            return next;
          });
        }}
      />

      <EditDeliveryDetailsModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        deliveryRecord={selectedEditRecord}
        isManualDelivery={activeTab === 'manual'}
        isCompletedTab={activeTab === 'completed' || !isEditAllowed}
        isEditAllowed={isEditAllowed}
        onSave={(updatedData, sectionName, callback) => {
          if (selectedEditRecord?.id) {
            const isManual = activeTab === 'manual' || Boolean(selectedEditRecord?.manualDeliver);
            const queryPath = getDeliveryQueryPath(selectedEditRecord.id, isManual);
            const customMsg = sectionName && sectionName !== 'All' ? `${sectionName} updated successfully` : 'Delivery details updated successfully';
            dispatch(
              ActionUpdateById(
                {
                  Query: queryPath,
                  obj: updatedData,
                  customMessage: customMsg,
                },
                (response: { status?: boolean; message?: string }) => {
                  if (response?.status !== false) {
                    setHasSavedChanges(true);
                  } else {
                    toast.error(response?.message || 'Failed to update delivery details');
                  }
                  if (typeof callback === 'function') {
                    callback(response);
                  }
                }
              )
            );
          } else if (typeof callback === 'function') {
            callback({ status: false });
          }
        }}
      />

      <AddManualDeliveryModal
        isOpen={isAddManualModalOpen}
        onClose={() => {
          setIsAddManualModalOpen(false);
          setSelectedEditRecord(null);
        }}
        deliveryRecord={selectedEditRecord}
        isReadOnly={!isEditAllowed}
      />

      <DeletePopModule
        isDeleteModelOpen={isDeleteModalOpen}
        setIsDeleteModelOpen={setIsDeleteModalOpen}
        updatedDataFun={handleConfirmDelete}
        message="Are you sure you want to delete this item."
      />
    </div>
  );
};

export default DeliveriesPage;
